import { useQuery } from '@apollo/client'
import React, { useMemo } from 'react'

import { CustomerLoyaltyCard } from '@src/components/CustomerLoyaltyCard/CustomerLoyaltyCard'
import { SelectWithArrow } from '@src/components/ServicePopover/SelectWithArrow'
import { allOutletDiscountsQuery } from '@src/hooks/sharedQueries/useAllDiscounts/queries/__generated__/getAllDiscounts.graphql-interface'
import { CustomerDetailsAndAddressesQuery } from '@src/hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/queries/__generated__/CustomerDetailsAndAddressesQuery.graphql-interface'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { convertToSlug } from '@src/utils/convertToSlug'
import { GetAddOnItemMenuGroupsForOutletQuery } from '../queries/__generated__/GetAddOnItemMenuGroupsForOutlet.graphql-interface'
import { useOutletLoyaltyCards } from '../../../hooks/useOutletLoyaltyCards'

import {
  ChevronContainer,
  MenuSubtitle,
  MenuTitle,
  StackedMenuContainer,
  StackedMenuOuterContainer,
  StyledLink,
  TitleAndSubtitleContainer,
} from './OutletStackedMenu.styles'

import { AddOnItemsOffer } from '../OutletDiscounts/AddOnItemsOffer'
import { SingleDiscount } from '../OutletDiscounts/SingleDiscount'
import { ScrollToContainer } from '../OutletMenu/MenuGroup/MenuGroup.styles'
import { MenuItemSearchResults } from '../OutletMenu/MenuItemSearchResults/MenuItemSearchResults'
import {
  DealsAndOffersContainer,
  SingleDiscountContainer,
} from '../OutletMenu/OutletMenuStyles'
import { GetOutletParentMenuIdsDocument } from '../OutletMenu/queries/__generated__/GetParentMenuIds.graphql-interface'
import { useOutletPageContext } from '../OutletPageContext'

interface OutletStackedMenuProps {
  allDiscounts: allOutletDiscountsQuery | undefined
  customerDetails: CustomerDetailsAndAddressesQuery | undefined
  addOnItemMenuGroups: GetAddOnItemMenuGroupsForOutletQuery | undefined
}

export const OutletStackedMenu: React.FC<OutletStackedMenuProps> = ({
  allDiscounts,
  customerDetails,
  addOnItemMenuGroups,
}) => {
  const { urlPath } = useMarketplace()
  const {
    showMenuSearchResults,
    outletFulfilment: {
      outlet,
      data: { currentFulfilment },
    },
  } = useOutletPageContext()

  const { loyaltyCards, isOutletInBasket, basketTotals } =
    useOutletLoyaltyCards(customerDetails)
  const allowAddOnItems = outlet.restaurant?.allowAddOnItems ?? false

  const { data: parentMenuGroupIds, previousData } = useQuery(
    GetOutletParentMenuIdsDocument,
    {
      variables: {
        outletId: outlet.id,
        endOfPrep: currentFulfilment.endOfPrep?.toISOString(),
        narrowFulfilmentMethods: currentFulfilment.narrowType,
      },
    }
  )

  const parentMenuItemGroups = useMemo(
    () =>
      (parentMenuGroupIds || previousData)?.menuItemGroupsForOutlet?.filter(
        menuItemGroup => !menuItemGroup.parentMenu
      ),
    [parentMenuGroupIds, previousData]
  )

  if (showMenuSearchResults)
    return <MenuItemSearchResults outletId={outlet.id} />

  const hasDealsSection = !!(
    allDiscounts?.allDiscountsByOutletId.length ||
    loyaltyCards?.length ||
    addOnItemMenuGroups?.menuItemGroupsForOutlet?.length
  )

  return (
    <>
      {hasDealsSection ? (
        <>
          <ScrollToContainer id="discountMenuItem" aria-hidden />
          <DealsAndOffersContainer>
            <SingleDiscountContainer>
              {loyaltyCards?.map(loyaltyCard => (
                <CustomerLoyaltyCard
                  loyaltyCard={loyaltyCard}
                  key={loyaltyCard.id}
                  basketTotals={
                    isOutletInBasket && basketTotals ? basketTotals : undefined
                  }
                />
              ))}
              {allDiscounts?.allDiscountsByOutletId.map(discount => (
                <div key={discount.id}>
                  <SingleDiscount allDiscounts={discount} />
                </div>
              ))}
              {allowAddOnItems &&
                addOnItemMenuGroups?.menuItemGroupsForOutlet
                  ?.filter(menuGroup => menuGroup.outletMenuItems?.length > 0)
                  ?.map(menuGroup => (
                    <AddOnItemsOffer
                      key={menuGroup.id}
                      parentMenuName={menuGroup.name}
                      addOnItemsMenuMinimumBasketTotal={
                        menuGroup.addOnItemsMenuMinimumBasketTotal
                      }
                    />
                  ))}
            </SingleDiscountContainer>
          </DealsAndOffersContainer>
        </>
      ) : null}
      <StackedMenuOuterContainer>
        {parentMenuItemGroups?.map(menu => (
          <StyledLink
            key={menu.id}
            to={`/${urlPath}/${outlet.id}/${convertToSlug(
              outlet.displayName
            )}/menu/${menu.id}`}
          >
            <StackedMenuContainer>
              <TitleAndSubtitleContainer>
                <MenuTitle>{menu.name}</MenuTitle>
                {menu.description && (
                  <MenuSubtitle>{menu.description}</MenuSubtitle>
                )}
              </TitleAndSubtitleContainer>
              <ChevronContainer>
                <SelectWithArrow justArrow id="caret" dataPresent={false} />
              </ChevronContainer>
            </StackedMenuContainer>
          </StyledLink>
        ))}
      </StackedMenuOuterContainer>
    </>
  )
}
